import { Text } from '@ui/themed';
import { t } from 'i18next';
import type { ReactNode } from 'react';
import { type ActivePlanKey, comparePlans } from '../constants/features';

/** Separators for the plan list, so the conjunction stays correct in every locale. */
const planListFormatter = new Intl.ListFormat(undefined, { style: 'long', type: 'conjunction' });

/** Highlights a single plan name inside the availability list. */
function PlanChip({ children }: { children?: ReactNode }) {
  return (
    <span className="inline-block rounded bg-linear-to-r from-gray-500 to-gray-900 px-1 py-px align-middle text-[10px] text-white">
      {children}
    </span>
  );
}

/**
 * "Available on: Starter, Growth, and Pro" — every plan gets its own chip, cheapest first
 * whatever order the caller passed. Plans that share a display name (`v2_basic` and
 * `v2_starter_plus` are both "Starter") collapse into a single chip.
 */
export function PlanAvailabilityBadge({ plans }: { plans: ActivePlanKey[] }) {
  const names = [
    ...new Set([...plans].sort(comparePlans).map((plan) => t(`labels:plans.${plan}`))),
  ];

  return (
    <span className="inline-flex items-center rounded-full border border-spark-border-default bg-gray-50 px-3 py-1">
      <Text variant="secondary">
        {t('labels:available_on')}{' '}
        {planListFormatter.formatToParts(names).map((part, index) =>
          part.type === 'element' ? (
            <PlanChip key={part.value}>{part.value}</PlanChip>
          ) : (
            // biome-ignore lint/suspicious/noArrayIndexKey: formatted separators are static text in a fixed order
            <span key={`separator-${index}`}>{part.value}</span>
          ),
        )}
      </Text>
    </span>
  );
}
